import { ContentState, EditorState, convertFromRaw, convertToRaw } from 'draft-js';
import { decorator } from '../components/RichEditor/utils';
import { isATitleBox, isATextBox, isAImgBox } from './boxes';
import { ItemTypes, RECURSIVE_WORK_POSITION_PLANNING_IMAGE_FIELDS } from '../constants/constants';
import { findWithRegex } from './dataSync';
import { getContainerColumnIds } from './columns';
import { FAILED_SCREENSHOT, PENDING_SCREENSHOT } from './planningScreenshots';

export function isRecursiveWorkPositionPlanningImageField(modelName, fieldKey) {
  return modelName === 'work_position' &&
    RECURSIVE_WORK_POSITION_PLANNING_IMAGE_FIELDS.includes(fieldKey);
}

/**
 * Image planning récursive sans data ("" ou variable non résolue). Seul le
 * rendu PDF la masque : l'éditeur garde la box, l'utilisateur y voit qu'aucune
 * donnée n'est arrivée et peut poser une image lui-même.
 */
export function shouldHideRecursivePlanningImageBox(box) {
  if (box.content?.recursiveRowId == null) return false;
  const adminValue = box.content?.adminValue || '';
  const parts = adminValue.split('.');
  if (parts.length !== 2 || !isRecursiveWorkPositionPlanningImageField(parts[0], parts[1])) {
    return false;
  }
  const src = box.content?.src;
  if (src == null || src === '') return true;
  // Capture en cours ou en échec : pas d'image à imprimer.
  if (src === PENDING_SCREENSHOT || src === FAILED_SCREENSHOT) return true;
  // Variable non résolue (#{work_position.planning_*}) => pas de data
  return !!findWithRegex(/#{.*?}/, src);
}

/**
 * `remapVariableKey` : optionnel. Reçoit la clé d'une variable document trouvée
 * dans le contenu et rend la clé à lui substituer, ou null pour la laisser
 * telle quelle. Sert à rattacher les variables du gabarit à la ligne courante
 * (cf. injectRecursiveModel : une copie par opportunité, chacune devant lire
 * les données de SON opportunité).
 */
export function substituteRecursiveVariables(
  sectionContent,
  row,
  modelName,
  { remapVariableKey } = {}
) {
  // Contenu injecté tel que conçu : lignes, colonnes et containers vides
  // compris, comme dans l'aperçu de la section.
  return {
    ...sectionContent,
    boxes: sectionContent.boxes.map((box) =>
      substituteInBox(box, row, modelName, remapVariableKey)
    ),
  };
}

// Les offsets et longueurs d'un raw draft-js se comptent en points de code
// (cf. UnicodeUtils.strlen côté draft), pas en unités UTF-16. Mesurer une valeur
// substituée avec String.length décalait tout ce qui la suit dès qu'elle
// contenait un caractère hors BMP (emoji).
const cpLength = (str) => Array.from(str).length;

function substituteInBox(box, row, modelName, remapVariableKey) {
  if (isAImgBox(box.type)) {
    if (box.content?.adminValue) {
      const parts = box.content.adminValue.split('.');
      if (parts.length === 2 && parts[0] === modelName) {
        const fieldKey = parts[1];
        const photoValue = row[fieldKey];
        // recursiveRowId = id de la ligne (poste) -> permet de re-cibler cette box
        // au broadcast WS pour swapper l'URL fraîche du screenshot (cf. store
        // refreshRecursivePlanningImages). src "" => loader le temps de la regen.
        const content = { ...box.content, recursiveRowId: row._id };
        if (photoValue != null) content.src = String(photoValue);
        return { ...box, content };
      }
    }
    const photoValue = row['photo'];
    if (photoValue != null) {
      return { ...box, content: { ...box.content, src: String(photoValue) } };
    }
    return box;
  }

  if (!isATextBox(box.type) && !isATitleBox(box.type)) return box;

  const raw = box.content?.raw;
  if (!raw) return box;

  let parsedRaw;
  try {
    parsedRaw = JSON.parse(raw);
  } catch {
    return box;
  }

  const { blocks, entityMap } = parsedRaw;

  // Map rawEntityKey -> replacement value for VARIABLE entities belonging to this model
  const substitutions = {};
  // { adminValue, src } pairs for inline IMAGE entities — applied via replaceEntityData after
  // convertFromRaw to avoid serializing Draft.js box objects through JSON.stringify
  const imageReplacements = [];
  // Map rawEntityKey -> nouvelle clé, pour les variables document rattachées à
  // la ligne courante (cf. remapVariableKey).
  const keyRemaps = {};
  let hasAny = false;
  Object.entries(entityMap).forEach(([rawKey, entity]) => {
    if (entity.type === 'VARIABLE') {
      const varKey = entity.data?.key ?? '';
      // Le préfixe seul ne suffit pas à reconnaître un champ du modèle : les
      // variables document occupent le même espace de noms (opportunity.NAME
      // côté API, opportunity.opportunity_name côté colonne). Substituer sans
      // vérifier effaçait le texte de toute variable document posée dans un
      // modèle récursif — la clé était introuvable dans la ligne, donc
      // remplacée par du vide. La sérialisation backend rend "" pour une
      // valeur nulle : la présence de la clé départage les deux sans risque.
      if (varKey.startsWith(`${modelName}.`)) {
        const fieldKey = varKey.slice(modelName.length + 1);
        if (Object.prototype.hasOwnProperty.call(row, fieldKey)) {
          substitutions[rawKey] =
            row[fieldKey] != null ? String(row[fieldKey]) : '';
          hasAny = true;
          return;
        }
      }
      // Pas un champ du modèle : variable document, qu'on rattache à la ligne
      // courante si l'appelant sait le faire.
      const remapped = remapVariableKey?.(varKey);
      if (remapped) {
        keyRemaps[rawKey] = remapped;
        hasAny = true;
      }
    } else if (entity.type === 'IMAGE') {
      // Inline image inserted via /image in a text box — box.type stays 'img', variable stored in adminValue
      const imgBox = entity.data?.box;
      if (!imgBox) return;
      const adminValue = imgBox.content?.adminValue ?? '';
      const parts = adminValue.split('.');
      if (parts.length !== 2 || parts[0] !== modelName) return;
      const photoValue = row[parts[1]];
      if (photoValue == null) return;
      imageReplacements.push({ adminValue, src: String(photoValue) });
      hasAny = true;
    }
  });

  if (!hasAny) return box;

  // Process blocks: substitute VARIABLE entity text ranges right-to-left to preserve offsets
  const newBlocks = blocks.map((block) => {
    const substitutionRanges = (block.entityRanges || []).filter((r) =>
      Object.prototype.hasOwnProperty.call(substitutions, String(r.key))
    );

    if (substitutionRanges.length === 0) return block;

    // Right-to-left splice keeps earlier offsets valid during iteration
    const sortedDesc = [...substitutionRanges].sort((a, b) => b.offset - a.offset);
    const chars = Array.from(block.text);
    for (const range of sortedDesc) {
      const replacement = substitutions[String(range.key)];
      chars.splice(range.offset, range.length, ...Array.from(replacement));
    }

    const sortedAsc = [...substitutionRanges].sort((a, b) => a.offset - b.offset);

    // Position dans le texte d'origine -> position dans le texte substitué.
    // Chaque substitution entièrement située avant la position la décale de
    // (longueur de la valeur - longueur du libellé). `isEnd` départage les bornes
    // qui tombent À L'INTÉRIEUR d'une variable : une plage qui commence dans la
    // valeur est ramenée à son début, une plage qui s'y termine est étendue à sa
    // fin — le style couvre alors toute la valeur, ce que l'utilisateur a désigné
    // en stylant le passage.
    const mapOffset = (pos, isEnd) => {
      let shift = 0;
      for (const sub of sortedAsc) {
        const replLength = cpLength(substitutions[String(sub.key)]);
        if (sub.offset + sub.length <= pos) {
          shift += replLength - sub.length;
        } else if (sub.offset < pos) {
          return sub.offset + shift + (isEnd ? replLength : 0);
        } else {
          break;
        }
      }
      return pos + shift;
    };

    // Entités non substituées (images inline notamment) : leur plage ne peut pas
    // chevaucher une variable, seul le point de départ bouge.
    const adjustedRanges = (block.entityRanges || [])
      .filter((r) => !Object.prototype.hasOwnProperty.call(substitutions, String(r.key)))
      .map((range) => {
        const offset = mapOffset(range.offset, false);
        return offset === range.offset ? range : { ...range, offset };
      });

    // Les plages de style suivent le texte au lieu d'être jetées : gras, souligné
    // et couleurs posés autour d'une variable survivaient jusqu'ici au modèle mais
    // disparaissaient de chaque ligne injectée. Une plage devenue vide (variable
    // résolue en chaîne vide) n'a plus rien à habiller et sort.
    const adjustedStyleRanges = (block.inlineStyleRanges || [])
      .map((range) => {
        const offset = mapOffset(range.offset, false);
        return { ...range, offset, length: mapOffset(range.offset + range.length, true) - offset };
      })
      .filter((range) => range.length > 0);

    return {
      ...block,
      text: chars.join(''),
      entityRanges: adjustedRanges,
      inlineStyleRanges: adjustedStyleRanges,
    };
  });

  const newEntityMap = { ...entityMap };
  Object.keys(substitutions).forEach((k) => delete newEntityMap[k]);
  // Seule la clé change : l'entité et sa plage restent en place, donc le texte
  // affiché reste le libellé tant que la valeur n'a pas été injectée.
  Object.entries(keyRemaps).forEach(([k, newKey]) => {
    newEntityMap[k] = {
      ...newEntityMap[k],
      data: { ...newEntityMap[k].data, key: newKey },
    };
  });

  const newRaw = JSON.stringify({ ...parsedRaw, blocks: newBlocks, entityMap: newEntityMap });

  try {
    const contentState = convertFromRaw(JSON.parse(newRaw));

    // Apply inline IMAGE src replacements via Draft.js entity API to avoid JSON serialization
    // of box objects (which may contain non-serializable fields after initializeBoxContent)
    if (imageReplacements.length > 0) {
      contentState.getBlockMap().forEach((block) => {
        block.findEntityRanges(
          (char) => char.getEntity() !== null,
          (start) => {
            const internalKey = block.getEntityAt(start);
            const entity = contentState.getEntity(internalKey);
            if (entity.getType() !== 'IMAGE') return;
            const imgBox = entity.getData()?.box;
            const match = imageReplacements.find(
              (r) => r.adminValue === (imgBox?.content?.adminValue ?? '')
            );
            if (!match) return;
            contentState.replaceEntityData(internalKey, {
              ...entity.getData(),
              box: { ...imgBox, content: { ...imgBox.content, src: match.src } },
            });
          }
        );
      });
    }

    const editorState = EditorState.createWithContent(contentState, decorator);
    return {
      ...box,
      content: {
        ...box.content,
        raw: newRaw,
        editorState,
        text: contentState.getPlainText(),
      },
    };
  } catch {
    // Rien à substituer (box entrée ici pour un simple changement de clé) : la
    // repli en texte brut la viderait, alors qu'il n'y avait rien à résoudre.
    if (Object.keys(substitutions).length === 0) return box;
    // Fallback: plain text with resolved variable values
    const plainText = Object.entries(substitutions).reduce(
      (acc, [, value]) => acc + value,
      ''
    );
    const contentState = ContentState.createFromText(plainText);
    const editorState = EditorState.createWithContent(contentState, decorator);
    return {
      ...box,
      content: {
        ...box.content,
        // Persist the raw of the plain-text fallback content. Was JSON.stringify(convertFromRaw)
        // (the imported function) -> undefined, which corrupted the box on save/reload.
        raw: JSON.stringify(convertToRaw(contentState)),
        editorState,
        text: plainText,
      },
    };
  }
}

export function isIndexedRecursiveKey(key, modelName) {
  const prefix = `${modelName}.`;
  if (!key.startsWith(prefix)) return false;
  const rest = key.slice(prefix.length);
  const dot = rest.indexOf('.');
  if (dot === -1) return false;
  return /^\d+$/.test(rest.slice(0, dot));
}


export function expandContentForRows(templateContent, rows, modelName) {
  if (!rows || rows.length === 0) return templateContent;

  const allBoxes = [];
  const allContainers = [];
  const allColumns = [];
  const allRowContainers = [];

  rows.forEach((row, rowIndex) => {
    const timestamp = Date.now() + rowIndex;
    const substituted = substituteRecursiveVariables(templateContent, row, modelName);

    // Lignes imbriquées : colonnes et lignes se référencent mutuellement
    // (rowContainerIds / parentColumnId / columnsIds), toutes deux sont
    // renommées à chaque copie.
    const rowContainerIdMap = {};
    const substitutedRows = substituted.rowContainers ?? [];
    substitutedRows.forEach((rowContainer) => {
      rowContainerIdMap[rowContainer.id] = `${rowContainer.id}-row${rowIndex}-${timestamp}`;
    });

    const colIdMap = {};
    substituted.columns.forEach((col) => {
      const newId = `${col.id}-row${rowIndex}-${timestamp}`;
      colIdMap[col.id] = newId;
      allColumns.push({
        ...col,
        id: newId,
        ...(col.rowContainerIds && {
          rowContainerIds: col.rowContainerIds.map((rId) => rowContainerIdMap[rId] ?? rId),
        }),
      });
    });

    substitutedRows.forEach((rowContainer) => {
      allRowContainers.push({
        ...rowContainer,
        id: rowContainerIdMap[rowContainer.id],
        parentColumnId: colIdMap[rowContainer.parentColumnId] ?? rowContainer.parentColumnId,
        columnsIds: rowContainer.columnsIds.map((cId) => colIdMap[cId] ?? cId),
      });
    });

    substituted.containers.forEach((container) => {
      allContainers.push({
        ...container,
        id: `${container.id}-row${rowIndex}-${timestamp}`,
        columnsIds: container.columnsIds.map((cId) => colIdMap[cId] ?? cId),
        dateTimestamp: timestamp,
      });
    });

    const boxIdMap = {};
    const rowBoxes = substituted.boxes.map((box) => {
      const newId = `${box.id}-row${rowIndex}-${timestamp}`;
      boxIdMap[box.id] = newId;
      return {
        ...box,
        id: newId,
        oldId: box.id,
        columnId: colIdMap[box.columnId] ?? box.columnId,
        dateTimestamp: timestamp,
      };
    });

    rowBoxes.forEach((box) => {
      if (box.type === ItemTypes.GROUP_BOXES && Array.isArray(box.boxIds)) {
        box.boxIds = box.boxIds.map((bid) => boxIdMap[bid] ?? bid);
      }
      delete box.oldId;
      allBoxes.push(box);
    });
  });

  return {
    ...templateContent,
    boxes: allBoxes,
    containers: allContainers,
    columns: allColumns,
    rowContainers: allRowContainers,
  };
}

export function getRowsFromTableResponse(tableResponse) {
  if (!tableResponse) return [];
  const rowKeys = Object.keys(tableResponse).filter((k) => k.startsWith('rows'));
  if (rowKeys.length === 0) return [];
  return rowKeys.flatMap((k) => tableResponse[k] ?? []);
}

export function getGroupedRowsFromTableResponse(tableResponse) {
  if (!tableResponse) return [];
  const rowKeys = Object.keys(tableResponse).filter((k) => k.startsWith('rows'));
  return rowKeys
    .map((k) => tableResponse[k] ?? [])
    .filter((group) => group.length > 0);
}

const WORK_TO_AGREEMENT_TYPE = {
  'guarding':            'security',
  'fire safety':         'security',
  'electronic security': 'electronic_security',
  'cleaning':            'cleaning',
  'accueil':             'accueil',
};

/**
 * Resolves the agreement category for a template.
 * Uses recursiveAgreementType when persisted; falls back to deriving from work.
 */
export function deriveAgreementType(template) {
  if (!template) return null;
  if (template.recursiveAgreementType) return template.recursiveAgreementType;
  if (template.work) return WORK_TO_AGREEMENT_TYPE[template.work] ?? 'other';
  return null;
}

/**
 * Child models that may be nested under a given parent model.
 * Uses the scoped agreement when known, otherwise the union across all
 * agreements (a template may not be bound to a convention yet).
 */
export function allowedChildModels(relationships, agreementType, parentModel) {
  if (!relationships || !parentModel) return [];
  if (agreementType && relationships[agreementType]) {
    return relationships[agreementType][parentModel] ?? [];
  }
  const union = new Set();
  Object.values(relationships).forEach((byParent) => {
    (byParent?.[parentModel] ?? []).forEach((child) => union.add(child));
  });
  return [...union];
}

export function shouldApplyRecursiveNestFilter(template) {
  return !!(
    template?.isRecursive &&
    template?.recursiveModelName &&
    template?.templatable !== 'offer'
  );
}

export function buildRecursiveNestMeta(relationships, template, parentModel) {
  if (!parentModel) return null;
  const allowed = parentModel === 'opportunity'
    ? null
    : new Set(allowedChildModels(relationships, deriveAgreementType(template), parentModel));
  return { parentModel, allowed };
}

export function isNestableRecursiveChild(meta, childModelName) {
  if (!meta) return true;
  if (!childModelName) return false;
  return meta.allowed === null
    ? childModelName !== meta.parentModel
    : meta.allowed.has(childModelName);
}

export function getSectionRecursiveMeta(section) {
  if (!section) return { isRecursive: false, recursiveModelName: null };
  return {
    isRecursive: !!(section.isRecursive ?? section.content?.isRecursive),
    recursiveModelName:
      section.recursiveModelName ?? section.content?.recursiveModelName ?? null,
  };
}

export function buildRecursiveFieldName(modelName, fieldLabel, t) {
  const modelLabel = t(`recursive_sections.model_labels.${modelName}`, { defaultValue: modelName });
  const modelPreposition = t(`recursive_sections.model_label_prepositions.${modelName}`, {
    defaultValue: `du ${modelLabel.toLowerCase()}`,
  });
  return t('recursive_sections.field_label_pattern', {
    fieldLabel,
    modelLabel: modelLabel.toLowerCase(),
    modelPreposition,
    defaultValue: `${fieldLabel} (${modelLabel})`,
  });
}

/** True when draft-js raw still has VARIABLE entities for this recursive model. */
export function hasUnresolvedRecursiveVariables(boxes, modelName, filters = {}) {
  if (!modelName || !boxes?.length) return false;
  const { fromSectionId, dateTimestamp, fromBoxId } = filters;
  const prefix = `${modelName}.`;
  const placeholder = `{{${prefix}`;

  return boxes.some((box) => {
    if (fromSectionId != null && box.fromSectionId !== fromSectionId) return false;
    if (dateTimestamp != null && box.dateTimestamp !== dateTimestamp) return false;
    if (fromBoxId != null && box.fromBoxId !== fromBoxId) return false;

    const raw = box.content?.raw;
    if (!raw) return false;
    try {
      const parsed = JSON.parse(raw);
      const hasEntity = Object.values(parsed.entityMap || {}).some(
        (entity) =>
          entity.type === 'VARIABLE' &&
          (entity.data?.key || '').startsWith(prefix) &&
          !isIndexedRecursiveKey(entity.data.key, modelName)
      );
      const hasText = (parsed.blocks || []).some((block) =>
        (block.text || '').includes(placeholder)
      );
      return hasEntity || hasText;
    } catch {
      return raw.includes(placeholder);
    }
  });
}

export function collectNoRepeatExclusions(content) {
  const excludedContainerIds = new Set();
  const excludedColumnIds = new Set();
  const excludedBoxIds = new Set();
  const masksById = new Map((content.masks || []).map((m) => [m.id, m]));
  const columnsById = new Map((content.columns || []).map((c) => [c.id, c]));

  for (const container of content.containers || []) {
    if (container.noRepeat) {
      excludedContainerIds.add(container.id);
      container.columnsIds?.forEach((id) => excludedColumnIds.add(id));
    }
  }
  for (const box of content.boxes || []) {
    if (box.noRepeat) excludedBoxIds.add(box.id);
  }

  for (const container of content.containers || []) {
    const hasNoRepeatMask = (container.columnsIds || []).some((colId) => {
      const col = columnsById.get(colId);
      return col?.maskId && masksById.get(col.maskId)?.noRepeat;
    });
    if (!hasNoRepeatMask) continue;
    excludedContainerIds.add(container.id);
    container.columnsIds?.forEach((id) => excludedColumnIds.add(id));
    if (container.linkedContainerId) {
      excludedContainerIds.add(container.linkedContainerId);
      const linked = (content.containers || []).find((c) => c.id === container.linkedContainerId);
      linked?.columnsIds?.forEach((id) => excludedColumnIds.add(id));
    }
  }

  for (const box of content.boxes || []) {
    if (excludedColumnIds.has(box.columnId)) excludedBoxIds.add(box.id);
  }

  return { excludedContainerIds, excludedColumnIds, excludedBoxIds };
}

/**
 * Retire du contenu d'une section ce que l'injection exclut (noRepeat, section
 * enfant injectée à part), sans toucher au reste : lignes, colonnes et
 * containers vides du modèle restent, l'injection doit rendre la section
 * telle qu'elle a été conçue.
 * - un container exclu part avec tout ce qu'il porte : colonnes, lignes
 *   imbriquées, sous-colonnes et box ;
 * - une box exclue part seule ;
 * - un container dont toutes les box sont exclues part aussi, pour ne pas
 *   laisser à leur place un blanc que le modèle n'a pas voulu.
 */
export function excludeFromContent(content, { containerIds = new Set(), boxIds = new Set() } = {}) {
  const containers = content.containers ?? [];
  const columns = content.columns ?? [];
  const rowContainers = content.rowContainers ?? [];
  const boxes = content.boxes ?? [];
  const subtreeColumnIds = (container) =>
    getContainerColumnIds({ container, columns, rowContainers });

  const removedContainerIds = new Set(containerIds);
  containers.forEach((container) => {
    if (removedContainerIds.has(container.id)) return;
    const columnIds = new Set(subtreeColumnIds(container));
    const containerBoxes = boxes.filter((b) => columnIds.has(b.columnId));
    if (containerBoxes.length && containerBoxes.every((b) => boxIds.has(b.id))) {
      removedContainerIds.add(container.id);
    }
  });

  const removedColumnIds = new Set(
    containers
      .filter((c) => removedContainerIds.has(c.id))
      .flatMap(subtreeColumnIds)
  );
  return {
    ...content,
    containers: containers.filter((c) => !removedContainerIds.has(c.id)),
    columns: columns.filter((c) => !removedColumnIds.has(c.id)),
    rowContainers: rowContainers.filter((r) => !removedColumnIds.has(r.parentColumnId)),
    boxes: boxes.filter((b) => !boxIds.has(b.id) && !removedColumnIds.has(b.columnId)),
  };
}

export function filterSectionContentNoRepeat(content) {
  const { excludedContainerIds, excludedBoxIds } = collectNoRepeatExclusions(content);
  if (excludedContainerIds.size === 0 && excludedBoxIds.size === 0) return content;
  return excludeFromContent(content, {
    containerIds: excludedContainerIds,
    boxIds: excludedBoxIds,
  });
}

/** Persisted boxes already expanded from this anchor (SECTION_LINK / SECTION_VARIABLE). */
export function isRecursiveSectionExpandedAtAnchor(boxes, anchorBoxId, sectionId) {
  if (!anchorBoxId || sectionId == null || !boxes?.length) return false;
  const sectionIdStr = String(sectionId);
  return boxes.some(
    (b) => b.fromBoxId === anchorBoxId && String(b.fromSectionId) === sectionIdStr
  );
}

/**
 * On proposal reload: skip re-expansion when content was saved already baked.
 * Template-first-open still expands (no baked children yet).
 *
 * Note: we intentionally do NOT check hasUnresolvedRecursiveVariables here.
 * substituteRecursiveVariables removes entity ranges from blocks but leaves
 * orphaned entries in entityMap, so hasUnresolvedRecursiveVariables always
 * returns true for previously-injected content — making the guard a no-op.
 * Baked children presence alone is the correct signal: first open = no children
 * = inject; reload = children exist = skip.
 */
export function shouldSkipRecursiveReloadExpansion(
  boxes,
  { anchorBoxId, sectionId }
) {
  return isRecursiveSectionExpandedAtAnchor(boxes, anchorBoxId, sectionId);
}

export function getBakedRecursiveInstances(boxes) {
  const seen = new Set();
  const instances = [];
  boxes.forEach((box) => {
    if (!box.fromSectionId) return;
    const key = `${box.fromSectionId}:${box.dateTimestamp ?? ''}:${box.fromBoxId ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    instances.push({
      fromSectionId: box.fromSectionId,
      dateTimestamp: box.dateTimestamp,
      fromBoxId: box.fromBoxId,
    });
  });
  return instances;
}
